import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BriefcaseBusinessIcon, CalendarClockIcon, CalendarDaysIcon, FileBadgeIcon, FileTextIcon, GraduationCapIcon, HouseIcon, MapPinIcon, ShieldAlertIcon, StoreIcon, TriangleAlertIcon, UserIcon, WalletIcon } from 'lucide-react';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Meter } from '../../components/ui/Meter';
import { WelcomeHero, type HeroStat } from '../../components/dashboard/WelcomeHero';
import { QuickAccessCard, type QuickAccessItem } from '../../components/dashboard/QuickAccessCard';
import { PremiumBanner } from '../../components/dashboard/PremiumBanner';
import { PowerWidget } from '../../components/power/PowerWidget';
import { useAccount } from '../../contexts/AccountContext';
import { useDataMode } from '../../contexts/DataModeContext';
import { FirstRun } from '../../components/platform/FirstRun';
import { dashboardImages } from '../../data/dashboardVisual';
import { assignments, attendance, cgpa, timetable } from '../../data/academics';
import { myApplications } from '../../data/housing';
import { rentLedger } from '../../data/property';
import { events } from '../../data/campus';
import { jobApplications } from '../../data/career';
import { alerts } from '../../data/support';
import { naira, percent } from '../../utils/format';
const TODAY = 'Thu';

/**
 * Six actions, chosen because they are the ones a student starts from rather
 * than arrives at. Each is a real destination — nothing here is a placeholder.
 */
const QUICK_ACTIONS: QuickAccessItem[] = [{
  to: '/academics',
  label: 'Academics',
  detail: 'Register, timetable, results',
  icon: GraduationCapIcon,
  tone: 'bg-brand-50 text-brand-700'
}, {
  to: '/money',
  label: 'Money',
  detail: 'Fees, bills, records',
  icon: WalletIcon,
  tone: 'bg-info-50 text-info-700'
}, {
  to: '/housing',
  label: 'Housing',
  detail: 'Search and applications',
  icon: HouseIcon,
  tone: 'bg-ok-50 text-ok-700'
}, {
  to: '/market',
  label: 'Marketplace',
  detail: 'Food, books, services, gigs',
  icon: StoreIcon,
  tone: 'bg-warn-50 text-warn-700'
}, {
  to: '/services',
  label: 'Campus services',
  detail: 'Transcripts, references',
  icon: FileBadgeIcon,
  tone: 'bg-[#f2e9fa] text-[#7e22ce]'
}, {
  to: '/opportunities',
  label: 'Opportunities',
  detail: 'Jobs and internships',
  icon: BriefcaseBusinessIcon,
  tone: 'bg-[#e7eafc] text-[#3730a3]'
}];
export function Home() {
  const {
    account,
    institutionName,
    campusName
  } = useAccount();
  const {
    isNew
  } = useDataMode();
  if (isNew) {
    return <FirstRun surface="Home" what="This is where Campuzon answers what you need to do today." why="Once your academic identity is verified and you have started applying, registering, or looking for somewhere to live, this page leads with whatever is closest to a deadline — and stays quiet when nothing is." note="Campuzon does not invent your timetable or your results. Those appear once your institution publishes them or you enter them yourself, and each one says which of the two it is." steps={[{
      label: 'Verify your academic identity',
      detail: 'It decides what you can prove to landlords, employers and communities. Nothing else here works as well without it.',
      to: '/identity'
    }, {
      label: 'Finish setting up your account',
      detail: 'Campus, programme, photo and what you want to hear about.',
      to: '/settings'
    }, {
      label: 'Find your people',
      detail: 'Join a course or department community so your campus feed has something in it.',
      to: '/social'
    }]} data-id="element-10663" data-mp="3759o8dy2agcw" />;
  }
  const todaysClasses = timetable.filter(session => session.day === TODAY);
  const openWork = assignments.filter(item => item.status !== 'graded' && item.status !== 'submitted').slice(0, 4);
  const atRisk = attendance.map(record => ({
    ...record,
    rate: percent(record.attended, record.held)
  })).filter(record => record.rate < record.requirement);
  const application = myApplications[0];
  const rentDue = rentLedger.find(record => record.tenant === account.fullName);
  const criticalAlert = alerts.find(alert => alert.severity === 'critical');
  const nextEvent = events.find(event => event.registeredByMe);
  const activeApplications = jobApplications.filter(item => item.stage !== 'Closed');
  const overdue = openWork.filter(item => item.status === 'overdue');
  const firstName = account.fullName.split(' ')[0];
  const stats: HeroStat[] = [{
    id: 'classes',
    value: todaysClasses.length,
    label: 'Classes today',
    to: '/academics'
  }, {
    id: 'tasks',
    value: openWork.length,
    label: 'Open tasks',
    to: '/academics'
  }, {
    id: 'overdue',
    value: overdue.length,
    label: 'Overdue',
    to: '/academics'
  }, {
    id: 'announcements',
    value: events.length,
    label: 'Announcements',
    to: '/social'
  }];
  return <div className="space-y-8" data-id="element-10664" data-mp="2gdazm9itxpwa">
      <WelcomeHero greeting="Good afternoon" firstName={firstName} programme={account.academic.programme} level={account.academic.level} institutionName={institutionName} campusName={campusName} status={account.academic.status} stats={stats} data-id="element-10665" data-mp="2gc85ztu4h9co" />

      {criticalAlert && <div className="flex flex-col gap-3 rounded-card border border-danger-100 bg-danger-50 px-5 py-4 sm:flex-row sm:items-center" data-id="element-10666" data-mp="1xeyoii8cy5y2">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white" data-id="element-10667" data-mp="1sgx2ppjjrsiw">
            <ShieldAlertIcon aria-hidden className="h-5 w-5 text-danger-700" data-id="element-10668" data-mp="3rrwuwqqxsig4" />
          </span>
          <div className="min-w-0 flex-1" data-id="element-10669" data-mp="3muy1kaf3kfiz">
            <p className="text-body font-semibold text-ink" data-id="element-10670" data-mp="3mrsf72xurrqy">{criticalAlert.title}</p>
            <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10671" data-mp="1l3eetl178jfx">{criticalAlert.detail}</p>
          </div>
          <Link to="/support" className="group inline-flex min-h-touch shrink-0 items-center gap-1.5 text-body-sm font-semibold text-danger-700 hover:text-danger-800" data-id="element-10672" data-mp="1pwqper91mppo">
            Campus safety
            <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10673" data-mp="23v74p6kz1ojf" />
          </Link>
        </div>}

      <section aria-labelledby="quick-access" data-id="element-10674" data-mp="1uush20kjbxcr">
        <h2 id="quick-access" className="text-h4 font-semibold text-ink" data-id="element-10675" data-mp="1utz7kru6ytqx">
          Quick access
        </h2>
        <ul className="mt-3.5 grid grid-cols-2 gap-3.5 md:grid-cols-3 xl:grid-cols-6" data-id="element-10676" data-mp="1yuats927d24i">
          {QUICK_ACTIONS.map(action => <li key={action.to} data-id="element-10677" data-mp="ngp48msfkyig">
              <QuickAccessCard item={action} data-id="element-10678" data-mp="ni05ofyelu6e" />
            </li>)}
        </ul>
      </section>

      {/* Power sits directly under quick access because it changes what a student
          does next — whether to set off at all. */}
      <PowerWidget data-id="element-10679" data-mp="b0fmud3ilmzn" />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]" data-id="element-10680" data-mp="pvlybfldqe0d">
        <div className="space-y-6" data-id="element-10681" data-mp="pt9gd2fmg3nb">
          <Panel title="Today — Thursday, 13 August" description="Your timetable and the work closest to its deadline." action={<Link to="/academics" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 transition-colors duration-150 ease-out hover:text-brand-800" data-id="element-10683" data-mp="3t9zte19mfgyi">
                View timetable
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10684" data-mp="23ybb8r8nn3d" />
              </Link>} bodyClassName="p-0" data-id="element-10682" data-mp="kvzf8pive3jn">
            {todaysClasses.length === 0 ? <div className="px-5 py-8 text-center" data-id="element-10685" data-mp="2k5rpn4ngwt6m">
                <CalendarDaysIcon aria-hidden className="mx-auto h-8 w-8 text-ink-subtle" data-id="element-10686" data-mp="2p3kthr1atb3q" />
                <p className="mt-3 text-body font-semibold text-ink" data-id="element-10687" data-mp="2u0jn88h1vg1e">No classes today</p>
                <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10688" data-mp="2y3gbaepd04cq">
                  Nothing is scheduled on your timetable for today.
                </p>
              </div> : <ol className="divide-y divide-line" data-id="element-10689" data-mp="380uevdq6oynm">
                {todaysClasses.map(session => <li key={session.id} className="flex gap-4 px-5 py-4" data-id="element-10690" data-mp="3crkmkxm31dlh">
                    {/* Time block reads as a timeline rather than a table cell. */}
                    <div className="w-16 shrink-0 border-l-2 border-brand-200 pl-3" data-id="element-10691" data-mp="3ht9ui4e0db5c">
                      <span className="cz-num block text-body font-semibold text-ink" data-id="element-10692" data-mp="3lulosn7tukjv">
                        {session.start}
                      </span>
                      <span className="cz-num mt-0.5 block text-caption text-ink-subtle" data-id="element-10693" data-mp="1f4s63l0drju5">
                        {session.end}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-10694" data-mp="163k68yi7uaxy">
                      <p className="text-body font-semibold text-ink" data-id="element-10695" data-mp="164dey50qjaiq">{session.title}</p>
                      <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm text-ink-muted" data-id="element-10696" data-mp="116253uafx00i">
                        <span className="rounded-md bg-canvas px-2 py-0.5 text-caption font-medium text-ink" data-id="element-10697" data-mp="1xu8523wf348g">
                          {session.courseCode}
                        </span>
                        <span className="inline-flex items-center gap-1" data-id="element-10698" data-mp="1p14z0go3ptnu">
                          <MapPinIcon aria-hidden className="h-4 w-4" data-id="element-10699" data-mp="1k2jwubpagzwr" />
                          {session.venue}
                        </span>
                        <span data-id="element-10700" data-mp="2hl3ifgdrxo8n">{session.mode}</span>
                      </p>
                    </div>
                    <CalendarDaysIcon aria-hidden className="mt-1 hidden h-5 w-5 shrink-0 text-ink-subtle sm:block" data-id="element-10701" data-mp="2cq7fvmawviha" />
                  </li>)}
              </ol>}

            <div className="border-t border-line bg-canvas px-5 py-4" data-id="element-10702" data-mp="1bda81heuizfw">
              <p className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-10703" data-mp="17avpkyf3a1p5">
                Due next
              </p>
              <ul className="mt-3 space-y-3" data-id="element-10704" data-mp="1l93sro2pxz8r">
                {openWork.map(item => <li key={item.id} className="flex items-start justify-between gap-4" data-id="element-10705" data-mp="1gaacxozylw5f">
                    <div className="min-w-0" data-id="element-10706" data-mp="1v0hrvuqrcx1d">
                      <p className="truncate text-body-sm font-semibold text-ink" data-id="element-10707" data-mp="1v2au8xuc8v3k">{item.title}</p>
                      <p className="text-caption text-ink-muted" data-id="element-10708" data-mp="1q380i9jt6baw">
                        {item.courseCode} · {item.weight}% of course mark
                      </p>
                    </div>
                    <Badge tone={item.status === 'overdue' ? 'danger' : item.dueLabel.startsWith('Tomorrow') ? 'warn' : 'neutral'} data-id="element-10709" data-mp="1z3mmzc8idwfz">
                      {item.dueLabel}
                    </Badge>
                  </li>)}
              </ul>
              <Link to="/academics" className="group mt-4 inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10710" data-mp="1zwu2js65wuvt">
                View all tasks
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10711" data-mp="1pwuvjzpe5cl2" />
              </Link>
            </div>
          </Panel>

          {atRisk.length > 0 && <Panel title="Attendance needs attention" description="Courses currently below the 75% requirement." action={<Link to="/academics" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10713" data-mp="3iv9zj79yqy9j">
                  View details
                  <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10714" data-mp="39t0ma9nb8qsg" />
                </Link>} data-id="element-10712" data-mp="1lwt38h5jpvig">
              <ul className="space-y-5" data-id="element-10715" data-mp="1esagd90cemz">
                {atRisk.map(record => <li key={record.courseCode} data-id="element-10716" data-mp="3slv2t80l0qys">
                    <div className="flex items-center gap-2" data-id="element-10717" data-mp="3sl36fuz5up7g">
                      <TriangleAlertIcon aria-hidden className="h-4 w-4 text-warn-700" data-id="element-10718" data-mp="2m62b9rhymb1x" />
                      <p className="text-body font-semibold text-ink" data-id="element-10719" data-mp="2h6pmh17j2twp">{record.courseCode}</p>
                      <span className="cz-num ml-auto text-body font-semibold text-ink" data-id="element-10720" data-mp="wonij75mol9d">
                        {record.rate}%
                      </span>
                    </div>
                    <div className="mt-2" data-id="element-10721" data-mp="11l45hy13ctkv">
                      <Meter value={record.rate} tone="warn" height="thin" data-id="element-10722" data-mp="15otv8qti5h7h" />
                    </div>
                    <p className="cz-num mt-1.5 text-caption text-ink-muted" data-id="element-10723" data-mp="1agtyas4vcq8a">
                      {record.attended} of {record.held} classes attended · {record.requirement}%
                      required to sit the exam
                    </p>
                  </li>)}
              </ul>
            </Panel>}

          <Panel title="Campus updates" description="What your campus and communities published recently." action={<Link to="/social" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10725" data-mp="3r57ksrvbdcp3">
                View all
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10726" data-mp="2zu9uhm52r4fl" />
              </Link>} bodyClassName="p-0" data-id="element-10724" data-mp="1pduj0bon7kx9">
            <ul className="divide-y divide-line" data-id="element-10727" data-mp="34uo0kyn484az">
              {events.slice(0, 3).map(event => <li key={event.id} className="flex items-start gap-4 px-5 py-4 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-10728" data-mp="2h4ler09snukt">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50" data-id="element-10729" data-mp="2qvo97j87xbb4">
                    <CalendarClockIcon aria-hidden className="h-4 w-4 text-brand-700" data-id="element-10730" data-mp="2m3enygi1szf6" />
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-10731" data-mp="1ydangi05pfse">
                    <p className="text-body font-semibold text-ink" data-id="element-10732" data-mp="1tg2268jpovmx">{event.title}</p>
                    <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10733" data-mp="8xq7gooy0bsu">
                      {event.dateLabel}, {event.time} · {event.venue}
                    </p>
                  </div>
                  {event.registeredByMe && <Badge tone="ok" data-id="element-10734" data-mp="3w2pkgjqq8ze3">Registered</Badge>}
                </li>)}
            </ul>
          </Panel>
        </div>

        <div className="space-y-6" data-id="element-10735" data-mp="3zgbibve9766">
          {/* Image-led card: a property is a place, so it leads with a photograph. */}
          <Panel title="Housing" action={<Link to="/housing" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10737" data-mp="wm9gj6ftgslz">
                View all
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10738" data-mp="noqqfnnbfo38" />
              </Link>} bodyClassName="p-0" data-id="element-10736" data-mp="rp0tarft9ydv">
            <div className="flex gap-4 border-b border-line px-5 py-4" data-id="element-10739" data-mp="1dvlgyubvvv6q">
              <div className="min-w-0 flex-1" data-id="element-10740" data-mp="18xtljqc15qxs">
                <p className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-10741" data-mp="18y3dhjgmy864">
                  Application
                </p>
                <p className="mt-1.5 text-body font-semibold text-ink" data-id="element-10742" data-mp="105i2wqn5r7sd">{application.listingName}</p>
                <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10743" data-mp="v5vju0knexbn">
                  Submitted {application.submitted} · move-in {application.moveIn}
                </p>
                <Badge tone="brand" className="mt-2.5" data-id="element-10744" data-mp="q954jfgo45ra">
                  In review by the operator
                </Badge>
              </div>
              <img src={dashboardImages.housing} alt={`${application.listingName} building exterior`} loading="lazy" className="h-20 w-20 shrink-0 rounded-xl object-cover" data-id="element-10745" data-mp="h75q8ux1ffs1" />
            </div>
            {rentDue && <div className="border-b border-line px-5 py-4" data-id="element-10746" data-mp="3titjpoyreyt3">
                <p className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-10747" data-mp="3tg7azm04ud9a">
                  Rent
                </p>
                <p className="cz-num mt-1.5 text-h3 font-bold text-ink" data-id="element-10748" data-mp="7278ope4ms8x">{naira(rentDue.amount)}</p>
                <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10749" data-mp="b4am3jkcw6wu">
                  {rentDue.period} for {rentDue.unit} · due {rentDue.due}
                </p>
                <p className="mt-2 text-caption text-ink-subtle" data-id="element-10750" data-mp="3alii3xpnntah">
                  Payments are handled by HavaPay. Campuzon keeps the record only.
                </p>
              </div>}
            <div className="px-5 py-4" data-id="element-10751" data-mp="3oixexni8n3ch">
              <Link to="/housing" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10752" data-mp="3ok03vpw02vpr">
                Continue your housing search
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10753" data-mp="13uj5tfujizdj" />
              </Link>
            </div>
          </Panel>

          {nextEvent && <Panel title="You are registered for" action={<Link to="/community" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10755" data-mp="jgy4hy8ivwuw">
                  View calendar
                  <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10756" data-mp="17725dxubtii7" />
                </Link>} bodyClassName="p-0" data-id="element-10754" data-mp="oa116f8ljmfb">
              <div className="relative overflow-hidden px-5 py-4" data-id="element-10757" data-mp="390i8vvbiirul">
                <img src={dashboardImages.campus} alt="" loading="lazy" className="pointer-events-none absolute -bottom-2 right-0 h-24 w-40 object-contain opacity-80" data-id="element-10758" data-mp="34yoa5skvnxx2" />
                <div className="relative flex gap-3 pr-24" data-id="element-10759" data-mp="2l3lp7yxb58ot">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50" data-id="element-10760" data-mp="2l196vknn0wb8">
                    <CalendarClockIcon aria-hidden className="h-4 w-4 text-brand-700" data-id="element-10761" data-mp="2q03qupxccqwx" />
                  </span>
                  <div className="min-w-0" data-id="element-10762" data-mp="2g40njgqr6m8e">
                    <p className="text-body font-semibold text-ink" data-id="element-10763" data-mp="2g521pu32fouc">{nextEvent.title}</p>
                    <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10764" data-mp="225j07k4rfbr0">
                      {nextEvent.dateLabel}, {nextEvent.time}
                    </p>
                    <p className="text-body-sm text-ink-muted" data-id="element-10765" data-mp="d4mkx990tge">{nextEvent.venue}</p>
                  </div>
                </div>
              </div>
            </Panel>}

          {/* Number-first card: the figure is the content, the label supports it. */}
          <Panel title="Your activity" bodyClassName="p-0" data-id="element-10766" data-mp="18v2ncbrj1voh">
            <dl className="divide-y divide-line" data-id="element-10767" data-mp="1dndm3baj7bfg">
              {[{
              label: 'CGPA',
              value: `${cgpa.toFixed(2)} / 5.00`,
              to: '/academics'
            }, {
              label: 'Registered units',
              value: '11',
              to: '/academics'
            }, {
              label: 'Live job applications',
              value: String(activeApplications.length),
              to: '/opportunities'
            }, {
              label: 'Open feedback threads',
              value: '2',
              to: '/support'
            }].map(row => <div key={row.label} className="flex items-baseline justify-between px-5 py-3.5" data-id="element-10768" data-mp="1ilnlxz77b07e">
                  <dt className="text-body-sm text-ink-muted" data-id="element-10769" data-mp="1mm7ib0jy9zsh">
                    <Link to={row.to} className="hover:text-ink hover:underline" data-id="element-10770" data-mp="76s4y6l30mno">
                      {row.label}
                    </Link>
                  </dt>
                  <dd className="cz-num text-body font-bold text-ink" data-id="element-10771" data-mp="b6ahof2r1rff">{row.value}</dd>
                </div>)}
            </dl>
            <div className="border-t border-line px-5 py-3.5" data-id="element-10772" data-mp="3uw6ygmt7p2hg">
              <Link to="/profile" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10773" data-mp="dna3pxgeva4p">
                <UserIcon aria-hidden className="h-4 w-4" data-id="element-10774" data-mp="3h3qyxvzirmla" />
                View full activity
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10775" data-mp="3c55z3xo6j4xd" />
              </Link>
            </div>
          </Panel>

          <div className="rounded-card border border-info-100 bg-info-50 px-5 py-4" data-id="element-10776" data-mp="3m2bpt4svstx2">
            <p className="flex items-start gap-3 text-caption leading-5 text-ink-muted" data-id="element-10777" data-mp="2ychdqpgdz9dv">
              <FileTextIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-info-700" data-id="element-10778" data-mp="2tf7rtjs7hfus" />
              <span data-id="element-10779" data-mp="3j33hqut7or3e">
                <span className="block text-body-sm font-semibold text-ink" data-id="element-10780" data-mp="3j1inz7eq1c6l">Explain Campuzon</span>
                {institutionName} shares its course directory with Campuzon but not student records,
                so your grades and attendance are the ones you enter yourself.
              </span>
            </p>
          </div>
        </div>
      </div>

      <PremiumBanner data-id="element-10781" data-mp="303pibxcpqo5i" />
    </div>;
}